import { Box, Stack } from '@chakra-ui/react'
import { isWithinInterval } from 'date-fns'
import _ from 'lodash'
import { useMemo } from 'react'
import { FiltersT } from '..'
import diseaseMapping from '../apis/diseaseMapping'
import useTestPositivityData from '../apis/useTestPositivityData'
import { parseDate, useNormalizedData } from '../helpers'
import { HospitalisationsDashboard } from './HospitalisationsDashboard'
import LeftSection from './LeftSection'
import findPeaks from './PeakFinder'
import { TestPositivityChart } from './TestPositivityDashboardChart'
import { useOnDownload } from './useOnDownload'

export const MainSection = ({
  filters,
  changeDateRange,
}: {
  filters: FiltersT
  changeDateRange: (start: string, end: string) => void
}) => {
  const { filteredData, isFetched, isError, isLoading, onDownload } =
    useTestPositivityDashboardData({ filters })
  const peaksData = useMemo(
    () => filteredData.filter((x) => 'peak' in x),
    [filteredData]
  )

  return (
    <Stack direction='column' gap={2}>
      <Box flex={1}>
        <Stack border='1px solid' borderColor='gray.300' p={4} rounded='lg'>
          <LeftSection peaksData={peaksData} filters={filters} />
          <Box w='100%' border='1px solid' borderColor='gray.300' />
          <Box h='50vh'>
            <TestPositivityChart
              data={filteredData}
              title={`Test Positivity`}
              yAxisTitle='Test Positivity (%)'
              dateRange={filters.dateRange}
              changeDateRange={changeDateRange}
              dataStatus={{ isFetched, isError, isLoading }}
              onDownload={onDownload}
            />
          </Box>
        </Stack>

        <Box mb={2} />

        <Box h='50vh'>
          <HospitalisationsDashboard
            filters={filters}
            changeDateRange={changeDateRange}
          />
        </Box>

        {/* Hide this for now */}
        {/* <Box mb={2} />

        <Box h='50vh'>
          <IliAriSariDashboard
            filters={filters}
            changeDateRange={changeDateRange}
            country={filters.country}
          />
        </Box> */}
      </Box>
    </Stack>
  )
}

const useTestPositivityDashboardData = ({ filters }: { filters: FiltersT }) => {
  const { data, isLoading, isError, isFetched } = useTestPositivityData()
  const onDownload = useOnDownload(data, filters, 'Test_Positivity')

  const normalizedData = useNormalizedData(data ?? [])

  const filterDateInterval = useMemo(
    () => ({
      start: parseDate(filters.dateRange.start),
      end: parseDate(filters.dateRange.end),
    }),
    [filters.dateRange.end, filters.dateRange.start]
  )

  // Filter in separate memos to increase performance
  // Start based on the one that's changed least
  const filteredDisease = useMemo(
    () =>
      normalizedData.filter((x) =>
        filters.dataType.map((y) => y.value).includes(x.diseaseName)
      ),
    [filters.dataType, normalizedData]
  )

  const filteredCountry = useMemo(
    () => filteredDisease.filter((x) => x.countryAlpha3 === filters.country),
    [filteredDisease, filters.country]
  )

  // Calculate peak data here because we need the whole time dataset
  const peakedData = useMemo(
    () =>
      Object.entries(_.groupBy(filteredCountry, 'diseaseName'))
        .map(([diseaseName, group]) => {
          // Special case for COVID-19 cause it's irregular
          if (diseaseName === diseaseMapping['COVID-19']) {
            const peaks = findPeaks(
              group.map((x) => ({ x: x.date, y: x.value })),
              { dataInterval: 'weekly' }
            )
            const maxIndexes = peaks.map((x) => x.index)

            return group.map((x, i) =>
              maxIndexes.includes(i) ? { ...x, peak: true } : x
            )
          }

          // For all other cases, we count seasonal based from [Start of Sept -> End of May]

          // Find all seasonal periods in the data
          const seasonalPeriods = []
          const dates = group.map((item) => parseDate(item.date))
          const minDate = dates[0]
          const maxDate = dates[dates.length - 1]

          // Generate all seasonal periods that cover our data range
          let currentYear = minDate.getFullYear()
          const endYear = maxDate.getFullYear() + 1

          while (currentYear <= endYear) {
            // Sept to May (next year)
            const winterStart = new Date(currentYear, 8, 1) // September 1st
            const winterEnd = new Date(currentYear + 1, 4, 31) // May 31st next year
            seasonalPeriods.push({
              start: winterStart,
              end: winterEnd,
              type: 'winter',
            })

            currentYear++
          }

          // Find peaks for each seasonal period
          const peakIndexes = new Set()

          seasonalPeriods.forEach((period) => {
            // Filter data points that fall within this seasonal period
            const seasonalData = group.filter((item) => {
              const itemDate = parseDate(item.date)
              return isWithinInterval(itemDate, period)
            })

            if (seasonalData.length === 0) return

            // Find the item with the highest value in this seasonal period
            const maxValueItem = _.maxBy(seasonalData, 'value')
            const maxIndex = group.findIndex((item) => item === maxValueItem)
            if (maxIndex !== -1) {
              peakIndexes.add(maxIndex)
            }
          })

          return group.map((x, i) =>
            peakIndexes.has(i) ? { ...x, peak: true } : x
          )
        })
        .flat(),
    [filteredCountry]
  )

  const filteredData = useMemo(
    () =>
      peakedData.filter((x) =>
        isWithinInterval(parseDate(x.date), filterDateInterval)
      ),
    [filterDateInterval, peakedData]
  )

  return { filteredData, isLoading, isError, isFetched, onDownload }
}
